import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';

export interface Gebaeude {
  id: string;
  name: string;
  standort_id: string;
  status: number;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface Sektion {
  id: string;
  name: string;
  gebaeude_id: string;
  beschreibung?: string;
  stockwerk?: number;
  status: number;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
  gebaeude_name?: string; // From API join
}

export interface Raum {
  id: string;
  name: string;
  sektion_id: string;
  raumtyp?: string;
  groesse?: number;
  kapazitaet?: number;
  status: number;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreateGebaeudeRequest {
  name: string;
  standort_id: string;
}

export interface CreateSektionRequest {
  name: string;
  gebaeude_id: string;
  beschreibung?: string;
  stockwerk?: number;
}

export interface CreateRaumRequest {
  name: string;
  sektion_id: string;
  raumtyp?: string;
  groesse?: number;
  kapazitaet?: number;
}

export interface UpdateRequest {
  name: string;
  status?: number;
}

export interface RoomIndikator {
  id: string;
  room_id: string;
  indikator_id: string;
  name: string;
  schwellenwert: string;
  einheit: string;
  mehr_weniger: string;
  current_value: string;
  status: number;
  indikator_number?: number;
  system_id?: number;
  qr_code?: string;
  position_x?: number | null;
  position_y?: number | null;
  bemerkung?: string;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateRoomIndikatorRequest {
  room_id: string;
  indikator_id: string;
  name: string;
  schwellenwert: string;
  einheit: string;
  mehr_weniger: string;
  current_value: string;
  status?: number;
  indikator_number?: number;
  qr_code?: string;
  position_x?: number | null;
  position_y?: number | null;
  bemerkung?: string;
  created_by?: string | null;
}

export interface UpdateRoomIndikatorRequest {
  room_id?: string;
  indikator_id?: string;
  name?: string;
  schwellenwert?: string;
  einheit?: string;
  mehr_weniger?: string;
  current_value?: string;
  status?: number;
  indikator_number?: number;
  qr_code?: string;
  position_x?: number | null;
  position_y?: number | null;
  bemerkung?: string;
  created_by?: string | null;
}

export interface SettingsValue {
  id: string;
  caption: string;
}

// Legacy interfaces for backward compatibility
export interface Indicator {
  id: string;
  name: string;
  status: 'active' | 'warning' | 'critical' | 'inactive';
  type: string;
  value: number;
  unit: string;
  description?: string;
  last_updated?: string;
}

export interface ObjektStrukturNode {
  id: string;
  name: string;
  type: 'gebaeude' | 'sektion' | 'room' | 'indicator';
  level: number;
  expanded?: boolean;
  children?: ObjektStrukturNode[];
  indicators?: Indicator[];
  parent_id?: string;
  standort_id?: string;
  metadata?: {
    address?: string;
    floor?: string;
    area?: number;
    capacity?: number;
    description?: string;
  };
}

export interface ObjektStrukturResponse {
  result: number;
  data: ObjektStrukturNode[];
  total?: number;
  sec: string;
}

@Injectable({
  providedIn: 'root'
})
export class ObjektStrukturService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  // GEBÄUDE CRUD Operations
  getGebaeude(standortId: string): Observable<{ success: boolean; data: Gebaeude[] }> {
    return this.http.get<{ success: boolean; data: Gebaeude[] }>(`${this.apiUrl}/gebaeude/standort/${standortId}`);
  }

  getGebaeudeById(id: string): Observable<{ success: boolean; data: Gebaeude }> {
    return this.http.get<{ success: boolean; data: Gebaeude }>(`${this.apiUrl}/gebaeude/${id}`);
  }

  createGebaeude(request: CreateGebaeudeRequest): Observable<{ success: boolean; message: string; data: Gebaeude }> {
    return this.http.post<{ success: boolean; message: string; data: Gebaeude }>(`${this.apiUrl}/gebaeude`, request);
  }

  updateGebaeude(id: string, request: UpdateRequest): Observable<{ success: boolean; message: string; data: Gebaeude }> {
    return this.http.put<{ success: boolean; message: string; data: Gebaeude }>(`${this.apiUrl}/gebaeude/${id}`, request);
  }

  deleteGebaeude(id: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.apiUrl}/gebaeude/${id}`);
  }

  // Soft delete - set status to inactive
  softDeleteGebaeude(id: string, currentData?: any): Observable<{ success: boolean; message: string; data: Gebaeude }> {
    console.log('SoftDeleteGebaeude - API URL:', `${this.apiUrl}/gebaeude/${id}`);
    
    // Use DELETE endpoint for soft delete (backend handles status = 0)
    return this.http.delete<{ success: boolean; message: string; data: Gebaeude }>(`${this.apiUrl}/gebaeude/${id}`);
  }

  // SEKTION CRUD Operations
  getSektionen(gebaeudeId: string): Observable<{ success: boolean; data: Sektion[] }> {
    return this.http.get<{ success: boolean; data: Sektion[] }>(`${this.apiUrl}/sektion/gebaeude/${gebaeudeId}`);
  }

  // Get all sektionen for a standort (across all buildings)
  getSektionenByStandort(standortId: string): Observable<{ success: boolean; data: Sektion[] }> {
    return this.http.get<{ success: boolean; data: Sektion[] }>(`${this.apiUrl}/sektion/standort/${standortId}`);
  }

  getSektionById(id: string): Observable<{ success: boolean; data: Sektion }> {
    return this.http.get<{ success: boolean; data: Sektion }>(`${this.apiUrl}/sektion/${id}`);
  }

  createSektion(request: CreateSektionRequest): Observable<{ success: boolean; message: string; data: Sektion }> {
    return this.http.post<{ success: boolean; message: string; data: Sektion }>(`${this.apiUrl}/sektion`, request);
  }

  updateSektion(id: string, request: UpdateRequest): Observable<{ success: boolean; message: string; data: Sektion }> {
    return this.http.put<{ success: boolean; message: string; data: Sektion }>(`${this.apiUrl}/sektion/${id}`, request);
  }

  deleteSektion(id: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.apiUrl}/sektion/${id}`);
  }

  // Soft delete - set status to inactive
  softDeleteSektion(id: string, currentData?: any): Observable<{ success: boolean; message: string; data: Sektion }> {
    console.log('SoftDeleteSektion - API URL:', `${this.apiUrl}/sektion/${id}`);
    
    // Use DELETE endpoint for soft delete (backend handles status = 0)
    return this.http.delete<{ success: boolean; message: string; data: Sektion }>(`${this.apiUrl}/sektion/${id}`);
  }

  // RAUM CRUD Operations
  getRaeume(sektionId: string): Observable<{ success: boolean; data: Raum[] }> {
    return this.http.get<{ success: boolean; data: Raum[] }>(`${this.apiUrl}/raum/sektion/${sektionId}`);
  }

  getRaumById(id: string): Observable<{ success: boolean; data: Raum }> {
    return this.http.get<{ success: boolean; data: Raum }>(`${this.apiUrl}/raum/${id}`);
  }

  createRaum(request: CreateRaumRequest): Observable<{ success: boolean; message: string; data: Raum }> {
    return this.http.post<{ success: boolean; message: string; data: Raum }>(`${this.apiUrl}/raum`, request);
  }

  updateRaum(id: string, request: UpdateRequest): Observable<{ success: boolean; message: string; data: Raum }> {
    return this.http.put<{ success: boolean; message: string; data: Raum }>(`${this.apiUrl}/raum/${id}`, request);
  }

  deleteRaum(id: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.apiUrl}/raum/${id}`);
  }

  // Soft delete - set status to inactive
  softDeleteRaum(id: string, currentData?: any): Observable<{ success: boolean; message: string; data: Raum }> {
    console.log('SoftDeleteRaum - API URL:', `${this.apiUrl}/raum/${id}`);
    
    // Use DELETE endpoint for soft delete (backend handles status = 0)
    return this.http.delete<{ success: boolean; message: string; data: Raum }>(`${this.apiUrl}/raum/${id}`);
  }

  // Legacy methods for backward compatibility
  /**
   * Get full objekt struktur with indicators (legacy method)
   */
  getFullObjektStruktur(standortId: string): Observable<ObjektStrukturResponse> {
    // For now, return mock data. In production, this would call the API
    return of(this.getMockObjektStruktur(standortId));
  }

  /**
   * Get indicators for a specific room (legacy method)
   */
  getIndicatorsForRoom(roomId: string): Observable<{ result: number; data: Indicator[]; sec: string }> {
    // Mock indicators data
    const mockIndicators: Indicator[] = [
      {
        id: `${roomId}_ind_1`,
        name: 'Temperatur',
        status: 'active',
        type: 'sensor',
        value: 22.5,
        unit: '°C',
        description: 'Raumtemperatur',
        last_updated: new Date().toISOString()
      },
      {
        id: `${roomId}_ind_2`,
        name: 'Luftfeuchtigkeit',
        status: 'warning',
        type: 'sensor',
        value: 65,
        unit: '%',
        description: 'Relative Luftfeuchtigkeit',
        last_updated: new Date().toISOString()
      }
    ];

    return of({
      result: 1,
      data: mockIndicators,
      sec: new Date().toISOString()
    });
  }

  /**
   * Toggle node expansion state (legacy method)
   */
  toggleNode(node: ObjektStrukturNode): void {
    node.expanded = !node.expanded;
  }

  /**
   * Expand all nodes (legacy method)
   */
  expandAll(nodes: ObjektStrukturNode[]): void {
    nodes.forEach(node => {
      node.expanded = true;
      if (node.children && node.children.length > 0) {
        this.expandAll(node.children);
      }
    });
  }

  /**
   * Collapse all nodes (legacy method)
   */
  collapseAll(nodes: ObjektStrukturNode[]): void {
    nodes.forEach(node => {
      node.expanded = false;
      if (node.children && node.children.length > 0) {
        this.collapseAll(node.children);
      }
    });
  }

  /**
   * Search nodes by name or type (legacy method)
   */
  searchNodes(nodes: ObjektStrukturNode[], searchTerm: string): ObjektStrukturNode[] {
    const results: ObjektStrukturNode[] = [];
    
    nodes.forEach(node => {
      const matchesSearch = node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           node.type.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (matchesSearch) {
        results.push(node);
      }
      
      if (node.children && node.children.length > 0) {
        const childResults = this.searchNodes(node.children, searchTerm);
        results.push(...childResults);
      }
    });
    
    return results;
  }

  private getMockObjektStruktur(standortId: string): ObjektStrukturResponse {
    const mockData: ObjektStrukturNode[] = [
      {
        id: 'geb_1',
        name: 'Hauptgebäude A',
        type: 'gebaeude',
        level: 0,
        expanded: true,
        standort_id: standortId,
        metadata: {
          address: 'Musterstraße 1, 8010 Graz',
          description: 'Hauptverwaltungsgebäude'
        },
        children: [
          {
            id: 'sek_1',
            name: 'Erdgeschoss',
            type: 'sektion',
            level: 1,
            expanded: false,
            parent_id: 'geb_1',
            metadata: {
              floor: 'EG',
              description: 'Eingangsbereich und Empfang'
            },
            children: [
              {
                id: 'room_101',
                name: 'Empfang',
                type: 'room',
                level: 2,
                expanded: false,
                parent_id: 'sek_1',
                metadata: {
                  area: 45,
                  capacity: 10,
                  description: 'Empfangsbereich mit Rezeption'
                },
                children: []
              }
            ]
          }
        ]
      }
    ];

    return {
      result: 1,
      data: mockData,
      total: mockData.length,
      sec: new Date().toISOString()
    };
  }

  // ===== ROOM INDIKATOR METHODS =====

  /**
   * Get all room indikatoren for a specific room
   */
  getRoomIndikatoren(roomId: string): Observable<RoomIndikator[]> {
    return this.http.get<RoomIndikator[]>(`${this.apiUrl}/room-indikator?room_id=${roomId}`);
  }

  /**
   * Get all room indikatoren for a specific standort
   */
  getRoomIndikatorenByStandort(standortId: string, limit: number = 200): Observable<RoomIndikator[]> {
    return this.http.get<RoomIndikator[]>(`${this.apiUrl}/room-indikator?standort_id=${standortId}&limit=${limit}`);
  }

  /**
   * Get room indikator by ID
   */
  getRoomIndikatorById(id: string): Observable<RoomIndikator> {
    return this.http.get<RoomIndikator>(`${this.apiUrl}/room-indikator/${id}`);
  }

  /**
   * Create new room indikator
   */
  createRoomIndikator(data: CreateRoomIndikatorRequest): Observable<RoomIndikator> {
    return this.http.post<RoomIndikator>(`${this.apiUrl}/room-indikator`, data);
  }

  /**
   * Create new indicator (alias for createRoomIndikator for backward compatibility)
   */
  createIndicator(data: CreateRoomIndikatorRequest): Observable<RoomIndikator> {
    return this.createRoomIndikator(data);
  }

  /**
   * Update room indikator
   */
  updateRoomIndikator(id: string, data: UpdateRoomIndikatorRequest): Observable<RoomIndikator> {
    return this.http.put<RoomIndikator>(`${this.apiUrl}/room-indikator/${id}`, data);
  }

  /**
   * Delete room indikator (soft delete)
   */
  deleteRoomIndikator(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/room-indikator/${id}`);
  }

  // ===== SETTINGS METHODS =====

  /**
   * Get settings values for a specific category
   */
  getSettingsValues(category: string): Observable<SettingsValue[]> {
    return this.http.get<SettingsValue[]>(`${this.apiUrl}/settings/${category}`);
  }

  /**
   * Get all available indikatoren
   */
  getIndikatoren(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/indikator`);
  }

  /**
   * Get the maximum indikator_number for a specific standort
   */
  getMaxIndikatorNumber(standortId: string): Observable<{ maxNumber: number }> {
    return this.http.get<{ maxNumber: number }>(`${this.apiUrl}/room-indikator/max-number/${standortId}`);
  }

  /**
   * Assign indikator numbers for all room_indikatoren of a standort.
   * Used after creating a new indicator on the fly in the plan-draw component.
   */
  assignIndikatorNumbers(standortId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/room-indikator/standort/${standortId}/assign-numbers`, {});
  }
}
